import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilingual } from '../hooks/useBilingual';
import './DashboardPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tb, bilingualMode } = useBilingual();
  const [stats, setStats] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // week, month, year

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token || !user.id) {
      navigate('/login');
      return;
    }

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, token, user.id]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // 1. 기본 통계
      const statsRes = await fetch(`${API_BASE_URL}/api/v2/assessments/student/${user.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // 2. 활동 히트맵 (최근 90일)
      const activityRes = await fetch(`${API_BASE_URL}/api/v2/assessments/student/${user.id}/activity-heatmap?days=90`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (activityRes.ok) {
        const activityJson = await activityRes.json();
        setActivityData(activityJson);
      }

      // 3. 진도 차트
      const progressRes = await fetch(`${API_BASE_URL}/api/v2/assessments/student/${user.id}/progress-chart?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (progressRes.ok) {
        const progressJson = await progressRes.json();
        setProgressData(progressJson);
      }

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>{tb('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <h1 className="dashboard-title">{tb('dashboard.title')}</h1>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card xp-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-value">{stats?.total_xp || 0}</div>
              <div className="stat-label">{tb('dashboard.stats.totalXp')}</div>
              <div className="stat-level">{tb('dashboard.stats.level')} {stats?.current_level || 1}</div>
            </div>
          </div>

          <div className="stat-card streak-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-content">
              <div className="stat-value">{stats?.current_streak || 0}</div>
              <div className="stat-label">{tb('dashboard.stats.dayStreak')}</div>
              <div className="stat-sublabel">{tb('dashboard.stats.longest')}: {stats?.longest_streak || 0} {tb('dashboard.stats.days')}</div>
            </div>
          </div>

          <div className="stat-card questions-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{stats?.total_questions_solved || 0}</div>
              <div className="stat-label">{tb('dashboard.stats.questionsSolved')}</div>
              <div className="stat-sublabel">{stats?.total_correct_answers || 0} {tb('dashboard.stats.correct')}</div>
            </div>
          </div>

          <div className="stat-card accuracy-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <div className="stat-value">{stats?.accuracy || 0}%</div>
              <div className="stat-label">{tb('dashboard.stats.accuracy')}</div>
              <div className="stat-sublabel">{tb('dashboard.stats.keepItUp')}</div>
            </div>
          </div>
        </div>

        {/* Activity Heatmap */}
        <div className="dashboard-section">
          <h2 className="section-title">📅 {tb('dashboard.activity.title')} ({tb('dashboard.activity.last90Days')})</h2>
          <div className="heatmap-container">
            <ActivityHeatmap data={activityData} tb={tb} />
          </div>
        </div>

        {/* Progress Chart */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">📈 {tb('dashboard.progress.title')}</h2>
            <div className="period-selector">
              <button 
                className={period === 'week' ? 'active' : ''}
                onClick={() => setPeriod('week')}
              >
                {tb('dashboard.progress.week')}
              </button>
              <button 
                className={period === 'month' ? 'active' : ''}
                onClick={() => setPeriod('month')}
              >
                {tb('dashboard.progress.month')}
              </button>
              <button 
                className={period === 'year' ? 'active' : ''}
                onClick={() => setPeriod('year')}
              >
                {tb('dashboard.progress.year')}
              </button>
            </div>
          </div>
          <div className="chart-container">
            <ProgressChart data={progressData} tb={tb} />
          </div>
        </div>

        {/* Level Progress */}
        <div className="dashboard-section">
          <h2 className="section-title">⭐ {tb('dashboard.level.title')}</h2>
          <div className="level-progress-container">
            <div className="level-info">
              <div className="current-level">{tb('dashboard.level.current')} {stats?.current_level || 1}</div>
              <div className="next-level">→ {tb('dashboard.level.next')} {(stats?.current_level || 1) + 1}</div>
            </div>
            <div className="progress-bar-large">
              <div 
                className="progress-fill-large"
                style={{ width: `${stats?.progress_percentage || 0}%` }}
              ></div>
            </div>
            <div className="progress-text-large">
              {stats?.xp_progress || 0} / {stats?.xp_needed || 0} XP
              <span className="percentage">({stats?.progress_percentage || 0}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Activity Heatmap Component
const ActivityHeatmap = ({ data, tb }) => {
  // 최근 90일의 날짜 생성
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 89; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const allDates = generateDates();
  const dataMap = {};
  data.forEach(item => {
    dataMap[item.date] = item;
  });

  // 강도 계산 (0-4)
  const getIntensity = (count) => {
    if (count === 0) return 0;
    if (count <= 2) return 1;
    if (count <= 4) return 2;
    if (count <= 6) return 3;
    return 4;
  };

  return (
    <div className="heatmap-grid">
      {allDates.map(date => {
        const activity = dataMap[date];
        const count = activity ? activity.count : 0;
        const intensity = getIntensity(count);
        const xp = activity ? activity.xp : 0;

        return (
          <div
            key={date}
            className={`heatmap-cell intensity-${intensity}`}
            title={`${date}: ${count} ${tb('dashboard.activity.quizzes')}, ${xp} ${tb('dashboard.activity.xp')}`}
          ></div>
        );
      })}
    </div>
  );
};

// Progress Chart Component (간단한 바 차트)
const ProgressChart = ({ data, tb }) => {
  if (!data || data.labels.length === 0) {
    return <div className="no-data">{tb('dashboard.progress.noData')}</div>;
  }

  const maxXP = Math.max(...data.xp_earned, 1);
  const maxQuestions = Math.max(...data.questions_solved, 1);

  return (
    <div className="progress-chart">
      <div className="chart-legend">
        <span className="legend-item">
          <span className="legend-color xp-color"></span> {tb('dashboard.progress.xpEarned')}
        </span>
        <span className="legend-item">
          <span className="legend-color questions-color"></span> {tb('dashboard.progress.questionsSolved')}
        </span>
        <span className="legend-item">
          <span className="legend-color accuracy-color"></span> {tb('dashboard.progress.accuracy')} (%)
        </span>
      </div>
      
      <div className="chart-bars">
        {data.labels.map((label, index) => (
          <div key={index} className="chart-bar-group">
            <div className="bar-container">
              <div 
                className="bar xp-bar"
                style={{ height: `${(data.xp_earned[index] / maxXP) * 100}%` }}
                title={`${data.xp_earned[index]} XP`}
              >
                <span className="bar-value">{data.xp_earned[index]}</span>
              </div>
            </div>
            <div className="bar-label">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
